import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ListTableComponent, ButtonComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../../shared/modules/ng-zorro/ng-zorro.module';
import { CommonModule } from '@angular/common';
import { TimesheetFilterComponent } from "../../../timesheets/timesheet-filter/timesheet-filter.component";
import { AuthorizeButtonDirective } from '../../../../../shared/authorization/authorizeButton.directive';
import { PAGE_CONSTANTS } from '../../../../../shared/constants/page.constants';
import { TableRowActions } from '../../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../../shared/interfaces/list.interface';
import { AllowListAuthStore } from '../../../../../shared/authorization/allow-list-auth.store';
import { UserService } from '../../../../../services/user.service';
import { DialogConfig, DialogService } from '../../../../../shared/services/dialog.service';
import { ListService } from '../../../../../shared/services/list.service';
import { InvoiceFilterComponent } from './invoice-filter/invoice-filter.component';
import { AddInvoiceComponent } from '../../provider/provider-invoice/add-invoice/add-invoice.component';
import { environment } from '../../../../../../environments/environment';
import { TimesheetService } from 'src/app/services/timesheet.service';
import { PDFFormConfig } from 'src/app/interfaces/config';
import { HttpResponse } from '@angular/common/http';
import { EditpdfComponent } from '../../../../editpdf/editpdf.component';
import { DateTimeFormatterService } from '../../../../../shared/utils/date-time-formatter.service';

@Component({
  selector: 'ppl-invoices-staff-list',
  standalone: true,
  imports: [ListTableComponent, ButtonComponent, NgZorroModule, CommonModule,
    InvoiceFilterComponent, AuthorizeButtonDirective, AddInvoiceComponent, EditpdfComponent],
  templateUrl: './invoices-staff-list.component.html',
  styleUrl: './invoices-staff-list.component.css',
})
export class InvoicesStaffListComponent implements OnInit, OnDestroy {
  pageId: string = PAGE_CONSTANTS.STAFF_INVOICES;
  private dialogSvc = inject(DialogService);

  @ViewChild('confirmationDialog') confirmationDialog!: TemplateRef<any>;
  @ViewChild('addInvoiceDialog') addInvoiceDialog!: TemplateRef<any>;
  @ViewChild('pdfDialog') pdfDialogRef!: TemplateRef<any>;
  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
  isPageRestrictionLoaded: boolean = false;
  isReadOnly: boolean = false;
  invoices: any[] = [];
  hasSearched = false;
  actionButtons: TableRowActionsModel = new TableRowActions({ view: false });
  actionsToBeHidden: string[] = [];
  columnsToBeHidden: string[] = [];
  chequeNumbers: string[] = [];
  programName: string = '';
  addDialogId: string = '';
  mode: 'add' | 'edit' | 'view' | 'actions' = 'add';
  appliedFilters: any[] = [];
  selectedRow: any;
  selectedLineItem: any;
  invoiceId: string = '';
  pdfConfig?: PDFFormConfig;
  pdfFile?: string;
  pdfBlob?: Blob;
  showModal = false;
  modalBody = {
    pdfViewer: ''
  };
  // Explicit filter config for this page. Mirrors the InvoiceFilterComponent default set
  // (which this page used to fall back on) plus the Service Date picker, so enabling it here
  // does not change any other page that relies on that shared default.
  filterFields = [
    { field: 'Timeframe', show: true, default: '30 days' },
    { field: 'serviceDate', show: true, hideFuturePeriods: true },
    { field: 'status', show: true },
    { field: 'consumer', show: true },
    { field: 'consumerPplId', show: true },
    { field: 'provider', show: true },
    { field: 'providerPplId', show: true },
    { field: 'chequeNumber', show: true },
    { field: 'payPeriod', show: false },
    { field: 'servicePeriod', show: false },
    { field: 'processStatusLkp', show: true },
    { field: 'serviceCode', show: true },
    { field: 'crmNumber', show: false }
  ];

  cols = [
    {
      field: 'payPeriod',
      header: 'Pay Period',
      jsonpath: 'payPeriod',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'providerName',
      header: 'Provider Name',
      jsonpath: 'providerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'providerPplId',
      header: 'Provider PPL ID',
      jsonpath: 'providerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalCost',
      header: 'Gross Amount',
      jsonpath: 'totalCost',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'paymentDate',
      header: 'Payment Date',
      jsonpath: 'paymentDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'chequeAmount',
      header: 'Check Amount',
      jsonpath: 'chequeAmount',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'chequeNumber',
      header: 'Check Number',
      jsonpath: 'chequeNumber',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    }
  ];

  lineItemCols = [
    {
      field: 'invoiceDate',
      header: 'Invoice Date',
      jsonpath: 'invoiceDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'invoiceNumber',
      header: 'Invoice Number',
      jsonpath: 'invoiceNumber',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'serviceDates',
      header: 'Service Dates',
      jsonpath: 'serviceDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'consumerName',
      header: 'Participant Name',
      jsonpath: 'consumerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'consumerPplId',
      header: 'Participant PPL ID',
      jsonpath: 'consumerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalLines',
      header: 'Total Lines',
      jsonpath: 'totalLines',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'paidLines',
      header: 'Paid Lines',
      jsonpath: 'paidLines',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'pendedLines',
      header: 'Pending Lines',
      jsonpath: 'pendedLines',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'deniedLines',
      header: 'Denied Lines',
      jsonpath: 'deniedLines',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'notSubmitted',
      header: 'Not Submitted Lines',
      jsonpath: 'notSubmitted',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'inProgress',
      header: 'Review Lines',
      jsonpath: 'inProgress',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'readyForPayment',
      header: 'Ready For Payment Lines',
      jsonpath: 'readyForPayment',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    }
  ];

  // Tags the Service Dates cell so its comma separated value can render one date per line.
  serviceDatesCellClassResolver = (_rowData: any, col: any): string =>
    col?.field === 'serviceDates' ? 'service-dates-cell' : '';

  constructor(
    private userService: UserService,
    private listService: ListService,
    private allowListStore: AllowListAuthStore,
    private timesheetService: TimesheetService,
    private dateTimeFormatter: DateTimeFormatterService
  ) {
  }

  ngOnInit() {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.userService.setLeadEntry('invoice');
    this.setPageAccess();
  }

  onLoadData() {
    this.invoiceId = '';
    this.selectedLineItem = null;
  }

  onNewInvoice() {
    this.openInvoice('add');
  }

  openInvoice(mode: 'add' | 'edit' | 'view' | 'actions') {
    this.mode = mode;
    const dialogConfig: DialogConfig = {
      title: mode === 'add' ? 'Add Invoice' : 'View Invoice',
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.addDialogId = this.dialogSvc.openDialog(
      this.addInvoiceDialog,
      dialogConfig,
      () => this.handleCancel({ isCloseTriggeredByDialog: true }),
      () => this.handleCancel({ isCloseTriggeredByDialog: true })
    );
  }

  onPageLoad(event: any) {
    this.searchInvoices(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, false, event.rows ?? 20);
  }

  getFilters(event: any) {
    const defaultDateFilter = new Date();
    defaultDateFilter.setDate(defaultDateFilter.getDate() - 30); // subtract days
    const filters = [];

    filters.push({
      jsonPath: "serviceDate",
      value: event.Timeframe ? event.Timeframe : defaultDateFilter.toLocaleDateString('en-US'),
      operator: "greaterThan"
    });

    if (event.serviceDate) {
      filters.push({
        jsonPath: "serviceDate",
        value: this.dateTimeFormatter.formatDateToString(event.serviceDate as Date),
        operator: "Equal"
      });
    }

    if (event.providerName && event.providerName.trim()) {
      filters.push({
        jsonPath: "providerName",
        value: event.provider.trim(),
        operator: "Equal"
      });
    }

    if (event.consumer && event.consumer.trim()) {
      filters.push({
        jsonPath: "consumerName",
        value: event.consumer.trim(),
        operator: "Equal"
      });
    }

    if (event.provider && event.provider.trim()) {
      filters.push({
        jsonPath: "providerName",
        value: event.provider.trim(),
        operator: "Equal"
      });
    }

    if (event.providerPplId && event.providerPplId.trim()) {
      filters.push({
        jsonPath: "providerPplId",
        value: event.providerPplId.trim(),
        operator: "Equal"
      });
    }

    if (event.consumerPplId && event.consumerPplId.trim()) {
      filters.push({
        jsonPath: "consumerPplId",
        value: event.consumerPplId.trim(),
        operator: "Equal"
      });
    }

    if (event.chequeNumber && event.chequeNumber.length) {
      filters.push({
        jsonPath: "chequeNumber",
        values: [event.chequeNumber],
        operator: "In"
      });
    }


    if (event.status) {
      filters.push({
        jsonPath: "businessStatus",
        value: event.status,
        operator: "Equal"
      });
    }

    if (event.servicePeriod) {
      filters.push({
        jsonPath: "servicePeriodId",
        value: event.payPeriod,
        operator: "Equal"
      });
    }

    if (event.payPeriod) {
      filters.push({
        jsonPath: "paidPayrollScheduleId",
        value: event.payPeriod,
        operator: "Equal"
      });
    }

    if (event.processStatusLkp) {
      filters.push({
        jsonPath: "processStatusLkp",
        value: event.processStatusLkp,
        operator: "Equal"
      });
    }

    if (event.serviceCode) {
      filters.push({
        jsonPath: "serviceCode",
        value: event.serviceCode,
        operator: "Equal"
      });
    }

    return filters;
  }

  onSearch(event: any) {
    const filters = this.getFilters(event);

    const hasRelevantFilter = filters.some(filter =>
      ["providerName", "consumerName", "providerPplId", "consumerPplId"].includes(filter.jsonPath)
    );


    this.appliedFilters = filters;
    this.searchInvoices(filters, undefined, undefined, event.chequeLookUp, this.childComponent?.recordsPerPage ?? 20);
  }

  onExport(_event: any) {
    if (!this.invoices || this.invoices.length === 0) {
      return;
    }

    const ids = [...new Set(this.invoices.map((r: any) => Number(r.payrollScheduleId)))];
    const headerIds = [...new Set(this.invoices.map((r: any) => Number(r.headerId)))];
    const filters: any = (this.appliedFilters || []).filter((f: any) => f.jsonPath !== 'serviceDuration');
    filters.push({
      jsonPath: 'id',
      values: ids,
      operator: 'In'
    });

    filters.push({
      jsonPath: 'headerId',
      values: headerIds,
      operator: 'In'
    });

    const processedPayload = this.buildRequestPayload(
      'invoiceExportList',
      'payrollScheduleId',
      filters,
      1,
      20
    );
    processedPayload.export = true;
    processedPayload.exportType = 'xlsx';
    this.listService
      .newListPage(processedPayload, `Invoices_Export_${new Date().toISOString()}.xlsx`, true)
      .subscribe((data: any) => {
      });
  }

  searchInvoices(filters: any, startingPageNumber = 1, currentPage = 0, isChequeLookUp = false, recordsPerPage = 20) {
    this.hasSearched = true;
    const processedPayload = this.buildRequestPayload(
      'invoice-payperiod',
      'payrollScheduleId',
      filters,
      startingPageNumber,
      recordsPerPage
    );
    this.listService
      .getListData(processedPayload, 'claims')
      .subscribe((data: any) => {
        const records = data.responsedata;
        records?.forEach((record: any) => {
          record.payPeriod = record.payrollStartDate + ' - ' + record.payrollEndDate;
        });

        if (isChequeLookUp) {
          if (records && records?.length) {
            this.chequeNumbers = Array.from(
              new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
          }
        }
        else {
          if (startingPageNumber > 1) {
            this.invoices = [...this.invoices, ...records];
          } else {
            this.invoices = records;
          }
          if (this.invoices?.length) {
            this.chequeNumbers = Array.from(
              new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
          }
        }
      });
  }

  openConfirmationPopup() {
    const dialogConfig: DialogConfig = {
      title: 'Error',
      showOkButton: true,
      okText: 'Ok',
      size: 'medium',
      position: 'top',
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.confirmationDialog,
      dialogConfig,
      () => this.handleCancel({ isCloseTriggeredByDialog: true }),
      () => this.handleCancel({ isCloseTriggeredByDialog: true })
    );
  }

  rowClicked(event: any) {
    if (event.loaded) {
      event.showLineItem = !event.showLineItem;
      return;
    }

    const subRowFilter = this.appliedFilters ? [...this.appliedFilters] : [];
    subRowFilter.push({
      jsonPath: "payrollScheduleId",
      value: event.payrollScheduleId,
      operator: "Equal"
    });

    subRowFilter.push({
      jsonPath: "providerId",
      value: event.providerId,
      operator: "Equal"
    });

    subRowFilter.push({
      jsonPath: "headerId",
      value: event.headerId,
      operator: "Equal"
    });

    this.selectedRow = event;
    this.getStaffInvoiceLineItems(subRowFilter, event);
  }

  getStaffInvoiceLineItems(filters: any, event: any) {
    const processedPayload = this.buildRequestPayload(
      'invoice-providerAndStaff',
      'payrollScheduleId',
      filters
    );
    this.listService
      .getListData(processedPayload, 'claims')
      .subscribe((data: any) => {
        const lineItems = data.responsedata;
        lineItems?.forEach((lineItem: any) => {
          lineItem.serviceDates = this.dateTimeFormatter.formatServiceDateList(lineItem.serviceDate);
        });
        event.lineItems = lineItems;
        event.showLineItem = true;
        event.loaded = true;
      });
  }

  fieldsUpdated(event: any) {
  }

  onAction(event: any) {
    if (event.action === 'view') {
      this.mode = 'view';
      this.selectedLineItem = event.data;
      this.invoiceId = event.data.invoiceHeaderId;
      this.onView();
    }
  }

  onView() {
    this.openInvoice('actions');
  }

  handleCancel(event: any) {
    if ((Array.isArray(event) && event[0]?.invoiceHeaderId) || event?.isCloseTriggeredByDialog) {
      this.dialogSvc.closeDialog(this.addDialogId);
      this.onLoadData();
    }
  }

  private buildRequestPayload(pageName: string, orderBy: string, searchParams: [], startingPageNumber = 1, recordsPerPage = 20) {
    if (searchParams.length == 0) {
      //handle the error here
      return;
    }

    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: recordsPerPage,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };
    return payload;
  }

  private setPageAccess() {
    const pageConfig = this.allowListStore.getConfig()?.pages[this.pageId];
    if (pageConfig) {
      const allowedActions = this.allowListStore.getAllowedActions(this.pageId);
      const allowedButtons = this.allowListStore.getAllowedButtons(this.pageId);
      this.isReadOnly = allowedActions.length === 0 && allowedButtons.length === 0;
    } else {
      this.isReadOnly = true;
    }
    this.isPageRestrictionLoaded = true;
  }

  onRowActions(event: any) {
    if (event.type === 'download') {
      this.timesheetService.downloadPaystub(event, this.programName);
    }
    else {
      this.viewPaystub(event, this.programName);
    }
  }

  viewPaystub(event: any, programCode: string) {
    this.showModal = true;
    this.modalBody.pdfViewer = 'Paystub';

    this.pdfConfig = {
      form_id: '',
      is_mandatory: false,
      form_description: '',
      form_name: '',
      fields: []
    };

    const dialogConfig: DialogConfig = {
      title: this.modalBody.pdfViewer,
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    }

    this.dialogSvc.openDialog(this.pdfDialogRef, dialogConfig, () => this.handlePDFDialogCancel(), () => this.handlePDFDialogCancel());

    this.getPaystub(event.event.checkId, event.event.chequeNumber, event.event.providerPplId, this.programName);
  }

  getPaystub(checkId: string, chequeNumber: string, empId: string, programCode: string) {
    this.timesheetService.getPayStub(checkId, chequeNumber, empId, programCode).subscribe((response: HttpResponse<Blob>) => {
      if (response.body) {
        const blob = new Blob([response.body], { type: response.body.type });
        this.pdfBlob = blob;
        this.pdfFile = window.URL.createObjectURL(blob);
      }
    });
  }

  handlePDFDialogCancel() {
    this.selectedLineItem = undefined;
    this.showModal = false;
    this.pdfConfig = undefined;
    this.pdfFile = undefined;
  }

  ngOnDestroy() {
    this.userService.setLeadEntry('');
  }
}
